import { useMemo, useRef, useState, useCallback } from 'react';
import { Client, IMessage, StompSubscription } from '@stomp/stompjs';
import { getApiUrl } from '@/lib/auth-utils';

type MessageHandler = (message: IMessage) => void;

export const useWebSocket = () => {
  const clientRef = useRef<Client | null>(null);
  const subscriptions = useRef<Map<string, StompSubscription>>(new Map());
  const handlers = useRef<Map<string, MessageHandler>>(new Map());
  const [isConnected, setIsConnected] = useState(false);

  const baseUrl = useMemo(
    () =>
      getApiUrl('ws').replace(
        /^http(s?):\/\//,
        'ws$1://'
      ),
    []
  );

  const resubscribeAll = useCallback((client: Client) => {
    for (const [destination, handler] of handlers.current.entries()) {
      const existing = subscriptions.current.get(destination);
      if (existing) {
        try {
          existing.unsubscribe();
        } catch {
          // best effort cleanup before re-subscribe
        }
      }
      const subscription = client.subscribe(destination, handler);
      subscriptions.current.set(destination, subscription);
    }
  }, []);

  const connect = useCallback(() => {
    const existingClient = clientRef.current;
    if (existingClient?.active || existingClient?.connected) return;
    if (!baseUrl) {
      console.error('STOMP broker URL is missing');
      return;
    }

    const client = new Client({
      brokerURL: baseUrl,
      reconnectDelay: 5000,
      debug: (str) => console.log('[STOMP]', str),
    });

    client.onConnect = () => {
      console.log('✅ WebSocket connected');
      setIsConnected(true);
      resubscribeAll(client);
    };

    client.onDisconnect = () => {
      console.log('❌ WebSocket disconnected');
      setIsConnected(false);
    };

    client.onWebSocketClose = () => {
      setIsConnected(false);
    };

    client.onWebSocketError = (event) => {
      console.error('🚨 WebSocket transport error:', event);
      setIsConnected(false);
    };

    client.onStompError = (frame) => {
      console.error('🚨 STOMP error:', frame.headers['message']);
    };

    client.activate();
    clientRef.current = client;
  }, [baseUrl, resubscribeAll]);

  const disconnect = useCallback(() => {
    const client = clientRef.current;
    if (client?.active || client?.connected) {
      void client.deactivate();
    }
    clientRef.current = null;
    subscriptions.current.clear();
    handlers.current.clear();
    setIsConnected(false);
  }, []);

  const subscribe = useCallback(
    (destination: string, handler: MessageHandler) => {
      const client = clientRef.current;

      handlers.current.set(destination, handler);

      if (client?.connected) {
        const subscription = client.subscribe(destination, handler);
        subscriptions.current.set(destination, subscription);
      } else {
        console.warn('⚠️ WebSocket not connected, subscription queued:', destination);
      }

      return () => {
        handlers.current.delete(destination);
        const subscription = subscriptions.current.get(destination);
        if (subscription) {
          try {
            subscription.unsubscribe();
          } catch (error) {
            console.debug('Skip unsubscribe during websocket shutdown', error);
          }
          subscriptions.current.delete(destination);
        }
      };
    },
    []
  );

  const publish = useCallback(
    (destination: string, body: any) => {
      const client = clientRef.current;

      if (!client || !client.connected) {
        console.warn('⚠️ WebSocket not connected');
        return;
      }

      client.publish({
        destination,
        body: JSON.stringify(body),
      });
    },
    []
  );

  return {
    connect,
    disconnect,
    subscribe,
    publish,
    isConnected,
  };
};